import React from 'react';
import * as moment from 'moment';
import { FORMAT } from 'web/components/utils/dateOfBirthValidation';
import PtlogoImage from "../../../../../assets/images/PalletTrader Plus_logoC.jpg";
import { Document, Page, Text, View, StyleSheet, Image } from '@react-pdf/renderer';
import { stateConvert } from 'web/components/utils/stateAbbreviation';

const styles = StyleSheet.create({
  container: { padding: '20px 50px 0', },
  header: { padding: '0 50px', },
  title: { fontSize: 15, padding: '10px 5px 5px', textAlign: 'center', color: "#00446e", },
  content: { display: 'flex', flexDirection: 'column', fontSize: '10px', },
  sectionWrapper: { display: 'flex', width: '100%', marginTop: '10px' },
  sectionHeader: { display: 'block', backgroundColor: '#00446e', borderRadius: "6px", color: '#ffffff', fontSize: '11px', padding: '7px 10px', },
  row: { display: 'flex', flexDirection: 'row', padding: '5px 10px', },
  halfRow: { display: 'flex', flexDirection: 'row', padding: '10px 10px 5px', },
  label: { width: '200px', fontSize: '11px', },
  value: { width: '50%', fontSize: '10px', color: '#000000e3' },
  signatureContainer: { display: 'flex', width: '100%', flexDirection: 'row', marginTop: "10px", gap: "3px", },
  signatureBox: { width: '50%', flexDirection: 'column', height: '120px', border: '1px solid #87cdf1', paddingBottom: '5px', },
  signatureText: { padding: '10px 5px 5px', },
  footerText: { padding: '5px', marginTop: "10px", },
  image: { width: '150px', },
});

const PdfDocument = ({ items }) => {

  const getSize = () => {
    const gradeName = items?.orderDetails?.grade?.gradeName || 'NA';
    if (gradeName === 'Custom') {
      const dim = items?.orderDetails?.dimension;
      if (dim?.length && dim?.width && dim?.height) {
        return `Custom (${dim.length}x${dim.width}x${dim.height})`;
      }
      return 'Custom';
    }
    return gradeName;
  };

  return (
    <Document>
      <Page size="A4">
        <View style={styles.container}>
          <Image style={styles.image} src={PtlogoImage} />
        </View>
        <View style={styles.header}>
          <Text style={styles.title}>Pallet Delivery Receipt</Text>
          <View style={styles.content}>
            <View style={styles.sectionWrapper}>
              <Text style={styles.sectionHeader}>Order Details:</Text>

              <View style={styles.halfRow}>
                <View style={styles.label}>
                  <Text>OD Number: </Text>
                </View>
                <View style={styles.value}>
                  <Text>{items.id}</Text>
                </View>
              </View>

              <View style={styles.row}>
                <View style={styles.label}>
                  <Text>PO Number: </Text>
                </View>
                <View style={styles.value}>
                  <Text>{items.poNumber}</Text>
                </View>
              </View>

              <View style={styles.row}>
                <View style={styles.label}>
                  <Text>Reference Number: </Text>
                </View>
                <View style={styles.value}>
                  <Text>{items.orderRefNumber || items?.referenceNumber || 'NA'}</Text>
                </View>
              </View>
            </View>

            <View style={{ display: 'flex', width: '100%', marginTop: '10px' }}>
              <Text style={styles.sectionHeader}>Consignee:</Text>

              {items.contactInfo && items.contactInfo.address && (
                <>
                  <View style={styles.halfRow}>
                    <View style={styles.label}>
                      <Text>Company Name: </Text>
                    </View>
                    <View style={styles.value}>
                      <Text>{items.contactInfo.address?.name}</Text>
                    </View>
                  </View>

                  <View style={styles.row}>
                    <View style={styles.label}>
                      <Text>Address: </Text>
                    </View>
                    <View style={styles.value}>
                      <Text>
                        {items.contactInfo.address.line1}
                        {items.contactInfo.address.line2 ? `, ${items.contactInfo.address.line2}` : ''}
                      </Text>
                    </View>
                  </View>

                  <View style={styles.row}>
                    <View style={styles.label}>
                      <Text>City/State/Zip: </Text>
                    </View>
                    <View style={styles.value}>
                      <Text>
                        {items.contactInfo.address.city}, {stateConvert(items.contactInfo.address.state) || items.contactInfo.address.state}, {items.contactInfo.address.zip}
                      </Text>
                    </View>
                  </View>
                </>
              )}
            </View>

            <View style={{ display: 'flex', width: '100%', marginTop: '10px' }}>
              <Text style={styles.sectionHeader}>Pallet Order Details:</Text>

              <View style={styles.halfRow}>
                <View style={styles.label}>
                  <Text>Condition: </Text>
                </View>
                <View style={styles.value}>
                  <Text>{items?.orderDetails?.condition?.length > 0 ? items.orderDetails.condition : 'NA'}</Text>
                </View>
              </View>

              <View style={styles.row}>
                <View style={styles.label}>
                  <Text>Material: </Text>
                </View>
                <View style={styles.value}>
                  <Text>{items?.orderDetails?.material || 'NA'}</Text>
                </View>
              </View>

              <View style={styles.row}>
                <View style={styles.label}>
                  <Text>Pallet Type: </Text>
                </View>
                <View style={styles.value}>
                  <Text>{items?.orderDetails?.commodity?.commodityName || 'NA'}</Text>
                </View>
              </View>

              <View style={styles.row}>
                <View style={styles.label}>
                  <Text>Pallet Size: </Text>
                </View>
                <View style={styles.value}>
                  <Text>{getSize()}</Text>
                </View>
              </View>

              <View style={styles.row}>
                <View style={styles.label}>
                  <Text>Grade: </Text>
                </View>
                <View style={styles.value}>
                  <Text>{items?.orderDetails?.grade?.sizeWiseGrad || 'NA'}</Text>
                </View>
              </View>

              <View style={styles.row}>
                <View style={styles.label}>
                  <Text>Moisture: </Text>
                </View>
                <View style={styles.value}>
                  <Text>{items.orderDetails.moistureContent || 'NA'}</Text>
                </View>
              </View>

              <View style={styles.row}>
                <View style={styles.label}>
                  <Text>Heat Treated: </Text>
                </View>
                <View style={styles.value}>
                  <Text>{items?.orderDetails?.isHeatTreared ? 'Yes' : 'No'}</Text>
                </View>
              </View>

              <View style={styles.row}>
                <View style={styles.label}>
                  <Text>Delivery Date: </Text>
                </View>
                <View style={styles.value}>
                  <Text>
                    {moment(items?.deliveryDate || items.contactInfo?.requestedDate).format('MM/DD/YYYY')}{' '}
                    {items.contactInfo?.requestedStartTime?.substring(0, 5)}-
                    {items.contactInfo?.requestedEndTime?.substring(0, 5)}
                  </Text>
                </View>
              </View>

              <View style={styles.row}>
                <View style={styles.label}>
                  <Text>Qty Ordered: </Text>
                </View>
                <View style={styles.value}>
                  <Text>{items?.orderDetails?.quantity}</Text>
                </View>
              </View>
            </View>

            <View style={{ display: 'flex', width: '100%', marginTop: '10px' }}>
              <Text style={styles.sectionHeader}>Signatures</Text>
              <View style={styles.signatureContainer}>
                <View style={styles.signatureBox}>
                  <Text style={styles.signatureText}>Service Provider Signature:</Text>
                  <Text style={styles.signatureText}>Provider Signature: _______________________</Text>
                  <Text style={styles.signatureText}>
                    I certify that the above named materials are shipped in full and meet the required specification.
                  </Text>
                </View>

                <View style={styles.signatureBox}>
                  <Text style={styles.signatureText}>Consignee:</Text>
                  <Text style={styles.signatureText}>Actual Delivered Qty___________</Text>
                  <Text style={styles.signatureText}>Date Received_________</Text>
                  <Text style={styles.signatureText}>Consignee Signature: _______________________</Text>
                  <Text style={styles.signatureText}>Consignee Name _______________________</Text>
                </View>
              </View>
            </View>

            <Text style={styles.footerText}>
              This document is not Bill of Lading and is only a Pallet Delivery Receipt. The terms and conditions for transporting
              these pallets are in a separate agreement between PalletTrader and the Service Provider. The Service Provider’s
              contract or its rates, tariffs, rules and regulations are not made a part of this Receipt. A copy of this Receipt must
              accompany the delivery to the Consignee.
            </Text>
          </View>
        </View>
      </Page>
    </Document>
  );
};

export default PdfDocument;